import React from 'react';
import { ResponsivePie } from '@nivo/pie';
import { useTranslation } from 'react-i18next';
import { toCurrencyString } from '@client/toolkit';
import { AgingBucket, AgingSummaryItemDto } from '@client/api';

const BUCKET_COLORS: Record<AgingBucket, string> = {
  NOT_DUE: '#CADDF4',
  DAYS_1_30: '#7DC3F0',
  DAYS_31_60: '#5092C2',
  DAYS_61_90: '#49B283',
  OVER_90: '#EB7370',
};

const BUCKET_LABEL_KEYS: Record<AgingBucket, string> = {
  NOT_DUE: 'outstandingPayment.bucket.notDue',
  DAYS_1_30: 'outstandingPayment.bucket.days1To30',
  DAYS_31_60: 'outstandingPayment.bucket.days31To60',
  DAYS_61_90: 'outstandingPayment.bucket.days61To90',
  OVER_90: 'outstandingPayment.bucket.over90',
};

interface PieChartProps {
  agingSummary: AgingSummaryItemDto[];
}

export function PieChart({ agingSummary }: PieChartProps) {
  const { t } = useTranslation('widget');

  const data = agingSummary.map((item) => ({
    id: t(`outstandingPayment.bucket.${item.bucket}`),
    value: item.amount.value,
    color: BUCKET_COLORS[item.bucket],
  }));

  const total = agingSummary.reduce((sum, item) => sum + item.amount.value, 0);

  return (
    <div className="h-64 bg-surface-base text-white relative">
      <ResponsivePie
        data={data}
        margin={{ top: 30, right: 60, bottom: 30, left: 60 }}
        innerRadius={0.93}
        colors={(d) => d.data.color}
        enableArcLabels={false}
        enableArcLinkLabels={false}
        borderWidth={0}
        isInteractive={false}
        legends={[
          {
            anchor: 'right',
            direction: 'column',
            symbolSize: 10,
            symbolShape: 'circle',
            itemWidth: 50,
            itemHeight: 10,
            itemsSpacing: 20,
            itemTextColor: '#fff',
            translateX: -10,
          },
        ]}
      />
      {/* Center Text */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
        <div className="text-sm text-white font-medium">{t('outstandingPayment.centerText')}</div>
        <div className="text-3xl font-bold">{toCurrencyString(total, undefined, { currency: 'NOK', maximumFractionDigits: 0 })}</div>
        <div className="text-xs text-white/70">{t('outstandingPayment.totalOutstandingTodaySubtitle')}</div>
      </div>
    </div>
  );
}
